import * as THREE from 'three';
import {
  IAlasimTermal,
  IKalipTermal,
  IKatilasmaGirdi,
  IKatilasmaMotoru,
  IKatilasmaSonuc,
  IMekanikOzet,
} from './IKatilasmaSim';
import { IVoxelKafes, voxelMerkez } from './IDokumSim';

/**
 * Faz 2 — Katılaşma (solidification) motoru.
 *
 * ŞEMA: Entalpi yöntemiyle geçici (transient) ısı iletimi, açık (explicit) FDM.
 *   Yönetici denklem:  ∂H/∂t = ∇·(k∇T)
 *   Hacimsel entalpi:  H_v(T) = ρ·cₚ·T + ρ·L·(1 − f_s(T))   [J/m³]
 *     - Sıvı (T ≥ T_liq):  H_v = ρcₚT + ρL
 *     - Katı (T ≤ T_sol):  H_v = ρcₚT
 *     - Mushy (arada):     gizli ısı f_s eğrisi boyunca salınır
 *
 * BİRİMLER (tutarlılık kritik):
 *   konum: kafes mm → çözümde metre (dx = hucreMm/1000)
 *   k [W/m·K], h [W/m²·K], ρ [kg/m³], cₚ [J/kg·K], L [J/kg], T [°C], t [s]
 *   A = dx² [m²], V = dx³ [m³], q [W], H_v [J/m³]
 *
 * SINIR KOŞULU (kalıp): dolu olmayan komşu = kalıp/dış →
 *   q = h·A·(T_kalıp − T_i)   (Newton soğuma, arayüz ısı transfer katsayısı h)
 *
 * KARARLILIK (explicit, 3D): Δt ≤ dx² / (2·d·α_max),  α = k/(ρcₚ),  d = 3.
 *   Güvenli Δt = 0.2 · dx² / (6·α_max).  α_max = k_max / (ρ·cₚ_min).
 *
 * İZLEME (per-voxel): T_solidus'u aşağı geçişte katılaşma süresi t_sol,
 *   o adımdaki soğuma hızı Ṙ [°C/s], sıcaklık gradyanı G [°C/mm] →
 *   Niyama  Ny = G / √Ṙ  [(°C·s)^½/mm]  (düşük = çekme gözenekliliği riski).
 *
 * KUSUR: sıcak nokta (en geç katılaşan, beslenme kontrolü), çekme (Niyama eşiği),
 *   sıcak yırtılma (Clyne-Davies CSC, kırılgan mushy aralık süre oranı).
 *
 * Saf/deterministik (Math.random yok). Typed array, sıcak döngüde allocation yok.
 * Performans: adım tavanı + opsiyonel termal grid downsample (büyük kafesler).
 */
export class FikoKatilasmaSimulasyon implements IKatilasmaMotoru {

  /** Explicit FDM adım tavanı (performans güvenlik freni) */
  private static readonly MAX_ADIM = 6000;
  /** Bu eşiğin üstündeki dolu voxel sayısında termal grid 2× downsample */
  private static readonly DOWNSAMPLE_ESIK = 250_000;
  /** Mutlak süre tavanı (maxSure verilmezse) — 30 dk */
  private static readonly VARSAYILAN_MAX_SURE = 1800;
  /** Niyama çekme eşiği (°C·s)^½/mm */
  private static readonly NIYAMA_ESIK = 2.0;
  /** Sıcak yırtılma (CSC) bölge eşiği */
  private static readonly YIRTILMA_ESIK = 0.5;

  async hesapla(girdi: IKatilasmaGirdi): Promise<IKatilasmaSonuc> {
    const { alasim, kalip } = girdi;

    // α_max önce (kararlılık + otomatik kabalaştırma kararı için).
    // α_max = k_max / (ρ · cₚ_min). T aralığını örnekleyerek tahmin et.
    let kMaxOn = 1e-6, cpMinOn = Infinity;
    const TminOn = Math.min(kalip.baslangicSicaklik, alasim.T_solidus);
    const TmaxOn = Math.max(alasim.dokumSicaklik, alasim.T_liquidus);
    for (let s = 0; s <= 10; s++) {
      const Ts = TminOn + (TmaxOn - TminOn) * (s / 10);
      const k = alasim.isiIletkenlik(Ts);
      const cp = alasim.ozguIsi(Ts);
      if (k > kMaxOn) kMaxOn = k;
      if (cp > 0 && cp < cpMinOn) cpMinOn = cp;
    }
    if (!isFinite(cpMinOn) || cpMinOn <= 0) cpMinOn = 500;
    const alphaMaxOn = kMaxOn / (alasim.yogunlukKgM3 * cpMinOn); // [m²/s]

    // Büyük kafes VEYA yetersiz zaman kapsaması → termal çözüm için 2^L kabalaştır.
    // dt ∝ dx² olduğundan kabalaştırma kararlılığı BOZMADAN uzun süre simüle eder.
    const calismaKafes = this.cozumKafesi(girdi, alphaMaxOn);
    const kafes = calismaKafes.kafes;
    const [nx, ny, nz] = kafes.boyut;
    const toplam = nx * ny * nz;
    const doluluk = kafes.doluluk;

    // dx: hücre kenarı metre cinsinden (mm → m)
    const dxMm = kafes.hucreMm;
    const dx = dxMm / 1000;
    const A = dx * dx;        // yüz alanı [m²]
    const V = dx * dx * dx;   // hücre hacmi [m³]
    const invV = 1 / V;

    const rho = alasim.yogunlukKgM3;            // ρ [kg/m³]
    const L = alasim.gizliIsi;                  // L [J/kg]
    const rhoL = rho * L;                        // [J/m³]
    const T_sol = alasim.T_solidus;
    const T_liq = alasim.T_liquidus;
    const T_kalip = kalip.baslangicSicaklik;
    const h = kalip.arayuzH;                      // [W/m²·K]
    const hA = h * A;                             // [W/K]

    // ── Soğutucu (chill) ısı çekiş alanı ───────────────────────────────
    // Chill voxelleri ekstra ısı kuyusuna sahip (yerel hızlı katılaşma) →
    // sıcak nokta/çekmeyi azaltır. T_kalip altına inmesi clamp ile engellenir.
    const chillH = (girdi.sogutucular && girdi.sogutucular.length)
      ? this.sogutucuAlani(kafes, girdi.sogutucular, hA) : null;

    // ── Besleyici tipi → sınır ısı-kaybı çarpanı alanı ─────────────────
    // Ekzotermik gömlek ~0.15 (ısı verir/tutar), yalıtımlı ~0.35, kör 0.9, açık 1.
    // Besleyici geç katılaşır → çekmeyi parça yerine besleyici karşılar (pipe).
    const bolgeTipi = kafes.bolgeTipi ?? null;
    const besleyiciIdxAlan = kafes.besleyiciIdx ?? null;
    let hFakAlan: Float32Array | null = null;
    if (bolgeTipi && besleyiciIdxAlan && girdi.besleyiciler?.length) {
      hFakAlan = new Float32Array(toplam).fill(1);
      const idxToCarpan = new Map<number, number>();
      for (const b of girdi.besleyiciler) idxToCarpan.set(b.idx, Math.max(0.05, b.hCarpan));
      for (let i = 0; i < toplam; i++) {
        if (doluluk[i] !== 1 || bolgeTipi[i] !== 1) continue;
        const c = idxToCarpan.get(besleyiciIdxAlan[i]);
        if (c != null) hFakAlan[i] = c;
      }
    }

    // ── Başlangıç sıcaklık + entalpi alanları ──────────────────────────
    const T = new Float32Array(toplam);        // mevcut sıcaklık [°C]
    const Tprev = new Float32Array(toplam);    // önceki adım [°C] (Ṙ için)
    const Hv = new Float32Array(toplam);       // hacimsel entalpi [J/m³]
    const baslangicT = calismaKafes.baslangicT;
    for (let i = 0; i < toplam; i++) {
      if (doluluk[i] !== 1) continue;
      const t0 = baslangicT ? baslangicT[i] : alasim.dokumSicaklik;
      T[i] = t0;
      Tprev[i] = t0;
      Hv[i] = this.entalpiHv(t0, alasim, rho, rhoL);
    }

    // ── Kararlılık: güvenli Δt ──────────────────────────────────────────
    const alphaMax = alphaMaxOn;
    const dtKararli = (dx * dx) / (6 * alphaMax); // explicit 3D teorik sınır
    // Δt ≤ dx²/(2·d·α), d=3 → güvenli 0.2·dx²/(6·α)
    let dt = 0.2 * dtKararli;
    if (!isFinite(dt) || dt <= 0) dt = 1e-4;

    // Süre tavanı: maxSure verilmişse onu kullan, adım sayısını sınırla.
    const maxSure = girdi.maxSure && girdi.maxSure > 0
      ? girdi.maxSure
      : FikoKatilasmaSimulasyon.VARSAYILAN_MAX_SURE;
    // Adım tavanına sığması için Δt'yi büyüt AMA ASLA kararlılık sınırını aşma —
    // aşarsak explicit FDM patlar (salınım) ve katılaşma sırası fizik dışı olur.
    // Kapsama yetmezse cozumKafesi zaten kabalaştırdı (dx↑ → dt↑).
    const maxAdim = FikoKatilasmaSimulasyon.MAX_ADIM;
    if (dt * maxAdim < maxSure) {
      dt = Math.min(maxSure / maxAdim, 0.9 * dtKararli);
    }

    // ── İzleme alanları ─────────────────────────────────────────────────
    const katilasmaSuresi = new Float32Array(toplam);   // t_sol [s]
    const niyama = new Float32Array(toplam);            // Ny [(°C·s)^½/mm]
    const sicakYirtilma = new Float32Array(toplam);     // CSC 0-1
    const sdasUm = new Float32Array(toplam);            // SDAS λ₂ [μm]
    const katilasti = new Uint8Array(toplam);           // T_sol'u geçti bayrağı
    const Ksdas = alasim.sdasKatsayi ?? 40;             // λ₂ = K·Ṙ^(−1/3)
    const gazEgilim = alasim.gazEgilim ?? 0.3;
    // Tane yapısı sayaçları (G > eşik → kolonsal/yönlü; düşük → eş eksenli)
    let kolonsalSay = 0, esEksenliSay = 0;
    const G_CET = 1.0; // °C/mm — kolonsal↔eş eksenli geçişi (Al için yaklaşık)
    // Clyne-Davies mushy aralık geçiş zamanları (f_s eşik geçişleri).
    // t40,t90,t99 = f_s 0.4 / 0.9 / 0.99'a iniş anları; -1 = henüz değil.
    const t_fs40 = new Float32Array(toplam).fill(-1);
    const t_fs90 = new Float32Array(toplam).fill(-1);
    const t_fs99 = new Float32Array(toplam).fill(-1);

    // Komşu lineer ofsetleri (+x,-x,+y,-y,+z,-z)
    const off = [1, -1, nx, -nx, nx * ny, -(nx * ny)];
    const nxny = nx * ny;

    // ── Sıcaklık anlık kareleri (termal heat-map animasyonu) ────────────
    // Çalışma gridinde kaydedilir; downsample varsa sonda ince gride yayılır
    // (kareUpscale) → görsel her zaman girdinin gridiyle hizalı. quantize [tMin..tMax].
    const kareKayit = true;
    const KARE_SAYI = 40;
    const tMinKare = Math.min(T_kalip, T_sol);
    const tMaxKare = Math.max(alasim.dokumSicaklik, T_liq);
    const tFark = Math.max(1, tMaxKare - tMinKare);
    const sicaklikKareler: { sure: number; T8: Uint8Array }[] = [];
    let kayitAralik = 1, kayitSayac = 0;
    const kareCek = (zaman: number) => {
      const T8 = new Uint8Array(toplam);
      for (let i = 0; i < toplam; i++) {
        if (doluluk[i] !== 1) continue;
        let q = ((T[i] - tMinKare) / tFark) * 255;
        T8[i] = q < 1 ? 1 : q > 255 ? 255 : q | 0; // 0 = dolu değil; dolu min 1
      }
      sicaklikKareler.push({ sure: zaman, T8 });
      if (sicaklikKareler.length >= 2 * KARE_SAYI) {
        // Log-incelme: her ikinciyi tut, aralığı ikiye katla (tüm süreyi kapsar)
        let w = 0;
        for (let r = 0; r < sicaklikKareler.length; r += 2) sicaklikKareler[w++] = sicaklikKareler[r];
        sicaklikKareler.length = w;
        kayitAralik *= 2;
      }
    };

    const ilerle = girdi.ilerle;
    let t = 0;
    let adim = 0;
    const doluToplam = kafes.doluSayi > 0 ? kafes.doluSayi : this.doluSay(doluluk);
    let kalanSivi = doluToplam;
    let sonIlerleme = -1;

    if (kareKayit) kareCek(0); // başlangıç (tüm parça sıcak)

    // ── Ana zaman döngüsü (explicit FDM) ────────────────────────────────
    const bPen = kalip.arayuzPenetrasyon ?? 0; // kum ısı penetrasyon katsayısı
    while (adim < maxAdim && t < maxSure && kalanSivi > 0) {
      // Tprev'i mevcut T'den kopyala (Ṙ = |T − Tprev| / Δt)
      Tprev.set(T);

      // Kalıp sınırı ısı transferi: semi-infinite kum varsa U(t)=1/(1/h+√(πt)/b),
      // yoksa sabit arayüz h. Kum ısındıkça (t↑) ısı çekişi azalır → gerçekçi süre.
      const UA = bPen > 0
        ? A / (1 / h + Math.sqrt(Math.PI * Math.max(t, dt)) / bPen)
        : hA;

      for (let i = 0; i < toplam; i++) {
        if (doluluk[i] !== 1) continue;
        const Ti = T[i];
        const ix = i % nx;
        const iy = ((i / nx) | 0) % ny;
        const iz = (i / nxny) | 0;
        const ki = alasim.isiIletkenlik(Ti);

        let Q = 0; // net ısı akısı [W]
        for (let c = 0; c < 6; c++) {
          // Sarma (wrap) kontrolü — eksen sınırında komşu yok = kalıp/dış
          let komsuDolu = true;
          if (c === 0 && ix + 1 >= nx) komsuDolu = false;
          else if (c === 1 && ix - 1 < 0) komsuDolu = false;
          else if (c === 2 && iy + 1 >= ny) komsuDolu = false;
          else if (c === 3 && iy - 1 < 0) komsuDolu = false;
          else if (c === 4 && iz + 1 >= nz) komsuDolu = false;
          else if (c === 5 && iz - 1 < 0) komsuDolu = false;

          if (komsuDolu) {
            const j = i + off[c];
            if (j >= 0 && j < toplam && doluluk[j] === 1) {
              // İletim: q = k_yüz·A·(T_j − T_i)/dx, k_yüz = (k_i+k_j)/2
              const Tj = T[j];
              const kYuz = (ki + alasim.isiIletkenlik(Tj)) * 0.5;
              Q += kYuz * A * (Tj - Ti) / dx;
              continue;
            }
          }
          // Dolu komşu yok → kalıp sınırı (arayüz+kum seri direnci).
          // Besleyici voxelinde tip çarpanı (ekzotermik/yalıtımlı ısıyı tutar).
          Q += UA * (hFakAlan ? hFakAlan[i] : 1) * (T_kalip - Ti);
        }

        // Soğutucu (chill) ekstra ısı kuyusu — yerel hızlı katılaşma
        if (chillH && chillH[i] > 0) Q += chillH[i] * (T_kalip - Ti);

        // ΔH_v = Q·Δt / V  [J/m³]
        Hv[i] += Q * dt * invV;
      }

      // ── H_v → T ters dönüşüm + izleme ─────────────────────────────────
      t += dt;
      for (let i = 0; i < toplam; i++) {
        if (doluluk[i] !== 1) continue;
        const Told = T[i];
        let Tyeni = this.sicaklikGeriCevir(Hv[i], alasim, rho, rhoL, T_sol, T_liq);
        // Chill voxeli kalıp sıcaklığının altına düşmesin (kararlılık + fizik)
        if (chillH && chillH[i] > 0 && Tyeni < T_kalip) {
          Tyeni = T_kalip;
          Hv[i] = this.entalpiHv(Tyeni, alasim, rho, rhoL);
        }
        T[i] = Tyeni;

        // Soğuma hızı Ṙ = |ΔT|/Δt [°C/s]
        const Rdot = Math.abs(Tyeni - Tprev[i]) / dt;

        // f_s eşik geçiş zamanları (Clyne-Davies için)
        const fs = alasim.katiFraksiyon(Tyeni);
        if (t_fs40[i] < 0 && fs >= 0.4) t_fs40[i] = t;
        if (t_fs90[i] < 0 && fs >= 0.9) t_fs90[i] = t;
        if (t_fs99[i] < 0 && fs >= 0.99) t_fs99[i] = t;

        // T_solidus'u AŞAĞI geçerken bir kez yakala
        if (!katilasti[i] && Tyeni <= T_sol && Told > T_sol) {
          katilasti[i] = 1;
          kalanSivi--;
          katilasmaSuresi[i] = t;

          // Gradyan G = |∇T| [°C/mm] — 6 komşu merkezi fark (mm cinsi dx)
          const ix = i % nx;
          const iy = ((i / nx) | 0) % ny;
          const iz = (i / nxny) | 0;
          const gx = this.merkeziFark(T, doluluk, i, 1, ix + 1 < nx, ix - 1 >= 0, off[0], off[1]);
          const gy = this.merkeziFark(T, doluluk, i, 1, iy + 1 < ny, iy - 1 >= 0, off[2], off[3]);
          const gz = this.merkeziFark(T, doluluk, i, 1, iz + 1 < nz, iz - 1 >= 0, off[4], off[5]);
          const G = Math.sqrt(gx * gx + gy * gy + gz * gz) / dxMm; // °C/mm

          // Niyama Ny = G / √Ṙ. Ṙ çok küçükse → büyük Niyama (risk).
          const RdotG = Rdot > 1e-6 ? Rdot : 1e-6;
          niyama[i] = G / Math.sqrt(RdotG);
          // SDAS λ₂ = K·Ṙ^(−1/3) [μm] — soğuma hızı düşük → kaba dendrit → zayıf
          sdasUm[i] = Ksdas * Math.pow(RdotG, -1 / 3);
          // Tane yapısı: yüksek gradyan = yönlü kolonsal (duvar), düşük = eş eksenli (merkez)
          if (G > G_CET) kolonsalSay++; else esEksenliSay++;
        }
      }

      adim++;

      // Termal kare yakala (log-incelme ile tüm süreyi kapsar)
      if (kareKayit) {
        if (kayitSayac % kayitAralik === 0) kareCek(t);
        kayitSayac++;
      }

      if (ilerle) {
        // İlerleme: katılaşma oranı (katı / dolu) ya da süre oranına göre.
        const oran = doluToplam > 0
          ? Math.min(100, Math.round((1 - kalanSivi / doluToplam) * 100))
          : Math.min(100, Math.round((t / maxSure) * 100));
        if (oran !== sonIlerleme) {
          sonIlerleme = oran;
          ilerle(oran);
        }
      }

      // UI'ya nefes ver (ana thread donmasın)
      if ((adim & 31) === 0) await new Promise(r => setTimeout(r, 0));
    }
    if (kareKayit) kareCek(t); // son kare

    // Döngü sonunda katılaşmamış kalanları (sıcak nokta merkezi) işaretle:
    // henüz T_sol altına inmemiş voxel'lerin katilasmaSuresi = son t.
    for (let i = 0; i < toplam; i++) {
      if (doluluk[i] === 1 && !katilasti[i]) {
        katilasmaSuresi[i] = t;
        // Hiç gradyan/Niyama yakalanmadıysa makul varsayılan (yüksek risk değil).
        if (niyama[i] === 0) niyama[i] = FikoKatilasmaSimulasyon.NIYAMA_ESIK * 2;
      }
    }

    // ── ÇEKME & BESLEME (shrinkage/feeding) simülasyonu ─────────────────
    // Katılaşan her hücre β·V hacim kaybeder; kayıp, BAĞLI sıvı bölgenin en
    // YÜKSEK noktasından (yerçekimi beslemesi) çekilir → besleyici seviyesi
    // zamanla düşer (pipe). Sıvı yolu kopan izole cep → parçada çekme boşluğu.
    let cekme: {
      cekmeBosluk: Float32Array;
      boslukKareler: { sure: number; B8: Uint8Array }[];
      besleyiciRapor: { id: string; kullanilanYuzde: number; hacimCm3: number; yeterli: boolean }[];
      parcaCekmeCm3: number;
      besleyiciCekmeCm3: number;
    } | null = null;
    if (girdi.cekmeOrani && girdi.cekmeOrani > 0) {
      cekme = this.cekmeBeslemeSim(
        kafes, katilasmaSuresi, sicaklikKareler.map(kr => kr.sure),
        girdi.cekmeOrani, girdi.yukari ?? new THREE.Vector3(0, 1, 0),
        girdi.besleyiciler ?? [],
      );
    }

    // ── Sıcak yırtılma (Clyne-Davies CSC) ───────────────────────────────
    // CSC = t_v / t_r,  t_v = (t99 − t90) kırılgan/serbest-besleme zorlanan,
    //   t_r = (t90 − t40) sıvı besleme mümkün aralık. Yüksek = riskli.
    this.clyneDavies(doluluk, toplam, t_fs40, t_fs90, t_fs99, sicakYirtilma);

    // ── Kusur bölgeleri ─────────────────────────────────────────────────
    const sicakNoktalar = this.sicakNoktaBul(kafes, katilasmaSuresi);
    const cekmeBolgeleri = this.cekmeBul(kafes, niyama, doluluk);
    const yirtilmaBolgeleri = this.bolgeKumele(
      kafes, sicakYirtilma, FikoKatilasmaSimulasyon.YIRTILMA_ESIK,
    );

    // ── Uyarılar (Türkçe özet) ──────────────────────────────────────────
    const uyarilar: string[] = [];
    const beslenmemis = sicakNoktalar.filter(s => !s.beslendi).length;
    if (cekmeBolgeleri.length > 0) {
      uyarilar.push(`${cekmeBolgeleri.length} çekme gözenekliliği bölgesi tespit edildi (Niyama < ${FikoKatilasmaSimulasyon.NIYAMA_ESIK}).`);
    }
    if (sicakNoktalar.length > 0) {
      uyarilar.push(`${sicakNoktalar.length} sıcak nokta bulundu, ${beslenmemis} tanesi beslenmemiş (çekme adayı).`);
    }
    if (yirtilmaBolgeleri.length > 0) {
      uyarilar.push(`${yirtilmaBolgeleri.length} sıcak yırtılma riskli bölge (Clyne-Davies CSC > ${FikoKatilasmaSimulasyon.YIRTILMA_ESIK}).`);
    }
    if (kalanSivi > 0) {
      uyarilar.push(`Süre tavanına ulaşıldı: ${kalanSivi} voxel hâlâ sıvı/katılaşmadı (süre ${t.toFixed(1)} s).`);
    }
    if (uyarilar.length === 0) {
      uyarilar.push('Belirgin döküm kusuru tespit edilmedi.');
    }

    // ── Mikroyapı + mekanik özellik (SDAS → Al kalibrasyonu) ────────────
    const mikroyapi = this.mekanikOzet(doluluk, toplam, sdasUm);
    if (mikroyapi) {
      const taneTop = kolonsalSay + esEksenliSay;
      if (taneTop > 0) {
        mikroyapi.kolonsalYuzde = Math.round((kolonsalSay / taneTop) * 100);
        mikroyapi.esEksenliYuzde = 100 - mikroyapi.kolonsalYuzde;
      }
      uyarilar.push(`Mikroyapı: SDAS ort. ${mikroyapi.sdasOrtUm.toFixed(0)} µm → UTS ~${mikroyapi.utsMpa.toFixed(0)} MPa, akma ~${mikroyapi.akmaMpa.toFixed(0)} MPa, uzama ~%${mikroyapi.uzamaYuzde.toFixed(1)} (Al referans).`);
    }

    // ── Makro çekme (beslenmeyen izole sıcak nokta) + gaz gözeneklilik ──
    const makroCekmeBolgeler = sicakNoktalar
      .filter(s => !s.beslendi)
      .map(s => ({ merkez: s.merkez.clone(), siddet: 1 }));
    if (makroCekmeBolgeler.length > 0) {
      uyarilar.push(`${makroCekmeBolgeler.length} makro çekme boşluğu adayı (beslenmeyen izole bölge).`);
    }
    // Gaz gözeneklilik: uzun katılaşma (yavaş soğuma) + alaşım H eğilimi
    let ortTsol = 0, sayG = 0;
    for (let i = 0; i < toplam; i++) { if (doluluk[i] === 1) { ortTsol += katilasmaSuresi[i]; sayG++; } }
    ortTsol = sayG ? ortTsol / sayG : 0;
    const gazGozeneklilik = Math.min(1, (ortTsol / Math.max(t, 1)) * gazEgilim * 1.5);
    if (gazGozeneklilik > 0.3) {
      uyarilar.push(`Gaz gözeneklilik riski: ${(gazGozeneklilik * 100).toFixed(0)}% (yavaş katılaşma + H eğilimi) — gazını al/dök sıcaklığını düşür.`);
    }

    // ── Artık gerilim + distorsiyon (farklı katılaşma → büzülme uyumsuzluğu) ──
    const gerilme = this.gerilmeAnaliz(kafes, katilasmaSuresi, doluluk, toplam, alasim, T_kalip);
    if (gerilme.bolgeler.length > 0) {
      uyarilar.push(`${gerilme.bolgeler.length} artık gerilim bölgesi — tepe ~${gerilme.maxGerilmeMpa.toFixed(0)} MPa (termoelastik tahmin); çarpılma ~${gerilme.distorsiyonMm.toFixed(2)} mm.`);
    }

    // ── Maça gazı (binder gas / blowhole) analizi ───────────────────────
    // Kum maça bağlayıcısı metal ısısıyla gazlaşır; maça çevresi uzun süre
    // sıvı kalırsa gaz metale kaçar → blowhole. Yakında hava deliği varsa tahliye.
    let macaGazBolgeleri: { merkez: THREE.Vector3; siddet: number }[] | undefined;
    if (girdi.macalar?.length) {
      macaGazBolgeleri = this.macaGazAnaliz(
        kafes, katilasmaSuresi, doluluk, t, girdi.macalar, girdi.havaDelikleri ?? [],
      );
      if (macaGazBolgeleri.length > 0) {
        uyarilar.push(`${macaGazBolgeleri.length} maça gazı (blowhole) riskli bölge — maça çevresi geç katılaşıyor; maça havalandırması/soğutucu ekleyin.`);
      }
    }

    // ── Çekme & besleme uyarıları ───────────────────────────────────────
    if (cekme) {
      for (const br of cekme.besleyiciRapor) {
        if (!br.yeterli) {
          uyarilar.push(`Besleyici %${br.kullanilanYuzde.toFixed(0)} tükendi — YETERSİZ, çekme parçaya kaçıyor. Besleyiciyi büyüt/ekzotermik gömlek kullan.`);
        } else if (br.kullanilanYuzde > 5) {
          uyarilar.push(`Besleyici çalışıyor: hacminin %${br.kullanilanYuzde.toFixed(0)}'i çekme telafisine gitti (pipe) — parça temiz besleniyor.`);
        }
      }
      if (cekme.parcaCekmeCm3 > 0.01) {
        uyarilar.push(`PARÇADA ${cekme.parcaCekmeCm3.toFixed(1)} cm³ çekme boşluğu kaldı (beslenemedi) — besleyici konumu/boyutu yetersiz.`);
      } else if (cekme.besleyiciCekmeCm3 > 0) {
        uyarilar.push(`Çekmenin tamamı (${cekme.besleyiciCekmeCm3.toFixed(1)} cm³) besleyicilere alındı — parça çekme boşluğu YOK. ✓`);
      }
    }

    // ── Sonuçları orijinal kafese geri ölçekle (downsample yapıldıysa) ──
    const cikis = calismaKafes.geriOlcekle(
      { katilasmaSuresi, niyama, sicakYirtilma, sdasUm },
    );
    // Termal + boşluk karelerini de ince gride yay (görsel hizası)
    const cikisKareler = calismaKafes.seviye > 0
      ? sicaklikKareler.map(kr => ({ sure: kr.sure, T8: calismaKafes.kareUpscaleU8(kr.T8) }))
      : sicaklikKareler;
    const cikisBoslukKareler = cekme
      ? (calismaKafes.seviye > 0
        ? cekme.boslukKareler.map(kr => ({ sure: kr.sure, B8: calismaKafes.kareUpscaleU8(kr.B8) }))
        : cekme.boslukKareler)
      : undefined;
    const cikisCekmeBosluk = cekme
      ? (calismaKafes.seviye > 0 ? calismaKafes.kareUpscaleF32(cekme.cekmeBosluk) : cekme.cekmeBosluk)
      : undefined;

    return {
      katilasmaSuresi: cikis.katilasmaSuresi,
      niyama: cikis.niyama,
      sicakYirtilma: cikis.sicakYirtilma,
      sdasUm: cikis.sdasUm,
      toplamSure: t,
      sicakNoktalar,
      cekmeBolgeleri,
      yirtilmaBolgeleri,
      mikroyapi,
      makroCekmeBolgeler,
      gazGozeneklilik,
      gerilmeBolgeler: gerilme.bolgeler,
      distorsiyonIndeks: gerilme.distorsiyon,
      gerilmeMpa: calismaKafes.seviye > 0 ? calismaKafes.kareUpscaleF32(gerilme.gerilmeMpa) : gerilme.gerilmeMpa,
      maxGerilmeMpa: gerilme.maxGerilmeMpa,
      distorsiyonMm: gerilme.distorsiyonMm,
      sicaklikKareler: cikisKareler,
      sicaklikMin: tMinKare,
      sicaklikMax: tMaxKare,
      cekmeBosluk: cikisCekmeBosluk,
      boslukKareler: cikisBoslukKareler,
      macaGazBolgeleri,
      besleyiciRapor: cekme?.besleyiciRapor,
      parcaCekmeCm3: cekme?.parcaCekmeCm3,
      besleyiciCekmeCm3: cekme?.besleyiciCekmeCm3,
      uyarilar,
    };
  }

  // ═══════════════════════════════════════════════════════════════════════
  // ÇEKME & BESLEME (shrinkage/feeding) — hacim muhasebesi
  // ═══════════════════════════════════════════════════════════════════════

  /**
   * Katılaşma-zamanı alanından çekme boşluğu oluşumunu hesaplar (MAGMA feeding
   * modülünün hafif eşdeğeri):
   *  1. Zaman kova kova ilerler (termal karelerin süreleriyle hizalı).
   *  2. Kovada katılaşan her hücre β·(kalan sıvı) kadar hacim talebi üretir.
   *  3. Talep, hücrenin BAĞLI olduğu sıvı bölgeden ve o bölgenin en YÜKSEK
   *     hücrelerinden karşılanır (yerçekimi beslemesi) → besleyici tepesi boşalır (pipe).
   *  4. Bağlı sıvı kalmadıysa talep hücrenin kendisinde boşluk olur (mikro/makro çekme).
   * Çıktı: per-voxel boşluk oranı (0-1) + zaman kareleri + besleyici raporu.
   */
  private cekmeBeslemeSim(
    kafes: IVoxelKafes,
    katilasmaSuresi: Float32Array,
    kareSureleri: number[],
    beta: number,
    yukari: THREE.Vector3,
    besleyiciler: { id: string; idx: number }[],
  ): {
    cekmeBosluk: Float32Array;
    boslukKareler: { sure: number; B8: Uint8Array }[];
    besleyiciRapor: { id: string; kullanilanYuzde: number; hacimCm3: number; yeterli: boolean }[];
    parcaCekmeCm3: number;
    besleyiciCekmeCm3: number;
  } {
    const [nx, ny, nz] = kafes.boyut;
    const toplam = nx * ny * nz;
    const nxny = nx * ny;
    const doluluk = kafes.doluluk;
    const bolgeTipi = kafes.bolgeTipi ?? null;
    const besIdx = kafes.besleyiciIdx ?? null;
    const uy = yukari.lengthSq() > 1e-6 ? yukari.clone().normalize() : new THREE.Vector3(0, 1, 0);

    const voidFrac = new Float32Array(toplam);   // 0 = tam metal, 1 = tam boş
    const etiket = new Int32Array(toplam);        // sıvı bileşen etiketi (kova başına)
    const yigin = new Int32Array(toplam);

    // Yükseklik (yukarı bileşen) — global azalan sırada tek sefer sırala.
    // e = merkez·yukari; indislerden doğrudan (origin sabit → katkısı sıra değiştirmez).
    const h = kafes.hucreMm;
    const doluLin: number[] = [];
    for (let i = 0; i < toplam; i++) if (doluluk[i] === 1) doluLin.push(i);
    const eDeger = new Float32Array(doluLin.length);
    for (let d = 0; d < doluLin.length; d++) {
      const i = doluLin[d];
      const ix = i % nx, iy = ((i / nx) | 0) % ny, iz = (i / nxny) | 0;
      eDeger[d] = (ix * uy.x + iy * uy.y + iz * uy.z) * h;
    }
    const sirali = doluLin.map((_, d) => d);
    sirali.sort((a, b) => eDeger[b] - eDeger[a]); // yüksekten alçağa

    const boslukKareler: { sure: number; B8: Uint8Array }[] = [];
    const kareCekB = (sure: number) => {
      const B8 = new Uint8Array(toplam);
      for (let d = 0; d < doluLin.length; d++) {
        const i = doluLin[d];
        const v = voidFrac[i];
        if (v > 0) B8[i] = v >= 1 ? 255 : (v * 255) | 0;
      }
      boslukKareler.push({ sure, B8 });
    };

    let oncekiT = -Infinity;
    const talepler = new Map<number, number>(); // etiket → hacim talebi (hücre birimi)

    for (const tk of kareSureleri) {
      // ── 1) KOVA BAŞI sıvı bileşenleri etiketle (ts > oncekiT & tam boş değil) ──
      // Bu kovada katılaşacak hücreler de kova başında sıvıydı → kendi etiketleri
      // var; talepleri bağlı bölgeden doğru çekilir (kova çözünürlüğü artefaktsız).
      etiket.fill(-1);
      let etiketSay = 0;
      for (let d = 0; d < doluLin.length; d++) {
        const s = doluLin[d];
        if (etiket[s] !== -1 || katilasmaSuresi[s] <= oncekiT || voidFrac[s] >= 1) continue;
        const e = etiketSay++;
        let sp = 0;
        yigin[sp++] = s; etiket[s] = e;
        while (sp > 0) {
          const u = yigin[--sp];
          const ux = u % nx, uyy = ((u / nx) | 0) % ny, uzz = (u / nxny) | 0;
          if (ux + 1 < nx) { const v = u + 1; if (doluluk[v] === 1 && etiket[v] === -1 && katilasmaSuresi[v] > oncekiT && voidFrac[v] < 1) { etiket[v] = e; yigin[sp++] = v; } }
          if (ux > 0) { const v = u - 1; if (doluluk[v] === 1 && etiket[v] === -1 && katilasmaSuresi[v] > oncekiT && voidFrac[v] < 1) { etiket[v] = e; yigin[sp++] = v; } }
          if (uyy + 1 < ny) { const v = u + nx; if (doluluk[v] === 1 && etiket[v] === -1 && katilasmaSuresi[v] > oncekiT && voidFrac[v] < 1) { etiket[v] = e; yigin[sp++] = v; } }
          if (uyy > 0) { const v = u - nx; if (doluluk[v] === 1 && etiket[v] === -1 && katilasmaSuresi[v] > oncekiT && voidFrac[v] < 1) { etiket[v] = e; yigin[sp++] = v; } }
          if (uzz + 1 < nz) { const v = u + nxny; if (doluluk[v] === 1 && etiket[v] === -1 && katilasmaSuresi[v] > oncekiT && voidFrac[v] < 1) { etiket[v] = e; yigin[sp++] = v; } }
          if (uzz > 0) { const v = u - nxny; if (doluluk[v] === 1 && etiket[v] === -1 && katilasmaSuresi[v] > oncekiT && voidFrac[v] < 1) { etiket[v] = e; yigin[sp++] = v; } }
        }
      }

      // ── 2) Bu kovada katılaşan hücrelerin çekme talebi (bileşenine yaz) ──
      talepler.clear();
      for (let d = 0; d < doluLin.length; d++) {
        const i = doluLin[d];
        const ts = katilasmaSuresi[i];
        if (ts <= oncekiT || ts > tk) continue;      // bu kovada katılaşmadı
        const talep = beta * (1 - voidFrac[i]);      // kalan metalin çekmesi
        if (talep <= 0) continue;
        const L = etiket[i];                          // kova başında sıvıydı → etiketi var
        if (L >= 0) talepler.set(L, (talepler.get(L) ?? 0) + talep);
      }

      // ── 3) Talepleri karşıla: ÖNCE hâlâ sıvı hücrelerden (en yüksekten —
      //       yerçekimi beslemesi, besleyici tepesi boşalır = pipe) ─────────
      if (talepler.size > 0) {
        for (let p = 0; p < sirali.length; p++) {
          const d = sirali[p];
          const i = doluLin[d];
          const L = etiket[i];
          if (L < 0 || katilasmaSuresi[i] <= tk) continue; // kova sonunda katı → sonra
          const kalanTalep = talepler.get(L);
          if (!kalanTalep || kalanTalep <= 1e-9) continue;
          const mevcut = 1 - voidFrac[i];
          if (mevcut <= 0) continue;
          const cek = Math.min(mevcut, kalanTalep);
          voidFrac[i] += cek;
          talepler.set(L, kalanTalep - cek);
        }
        // ── 4) Kalan talep: bileşenin tüm sıvısı bu kovada dondu (izole cep) →
        //       boşluk cebin EN YÜKSEK hücrelerinde toplanır (makro çekme boşluğu)
        for (let p = 0; p < sirali.length; p++) {
          const d = sirali[p];
          const i = doluLin[d];
          const L = etiket[i];
          if (L < 0 || katilasmaSuresi[i] > tk) continue; // bu kovada donanlar
          const kalanTalep = talepler.get(L);
          if (!kalanTalep || kalanTalep <= 1e-9) continue;
          const mevcut = 1 - voidFrac[i];
          if (mevcut <= 0) continue;
          const cek = Math.min(mevcut, kalanTalep);
          voidFrac[i] += cek;
          talepler.set(L, kalanTalep - cek);
        }
      }

      kareCekB(tk);
      oncekiT = tk;
    }

    // ── Rapor ────────────────────────────────────────────────────────────
    const hucreCm3 = (h * h * h) / 1000;
    let parcaCekme = 0, besleyiciCekme = 0;
    const besHacim = new Map<number, number>();   // idx → voxel sayısı
    const besKullanim = new Map<number, number>(); // idx → Σ voidFrac
    for (let d = 0; d < doluLin.length; d++) {
      const i = doluLin[d];
      const v = voidFrac[i];
      if (bolgeTipi && bolgeTipi[i] === 1 && besIdx) {
        const bi = besIdx[i];
        besHacim.set(bi, (besHacim.get(bi) ?? 0) + 1);
        if (v > 0) { besKullanim.set(bi, (besKullanim.get(bi) ?? 0) + v); besleyiciCekme += v; }
      } else if (v > 0) {
        parcaCekme += v;
      }
    }
    const besleyiciRapor = besleyiciler.map(b => {
      const hac = besHacim.get(b.idx) ?? 0;
      const kul = besKullanim.get(b.idx) ?? 0;
      const yuzde = hac > 0 ? (kul / hac) * 100 : 0;
      return {
        id: b.id,
        kullanilanYuzde: yuzde,
        hacimCm3: hac * hucreCm3,
        yeterli: yuzde < 95,
      };
    });

    return {
      cekmeBosluk: voidFrac,
      boslukKareler,
      besleyiciRapor,
      parcaCekmeCm3: parcaCekme * hucreCm3,
      besleyiciCekmeCm3: besleyiciCekme * hucreCm3,
    };
  }

  /**
   * SDAS alanından mikroyapı + mekanik özellik özeti (alüminyum döküm kalibrasyonu).
   * Küçük SDAS → ince dendrit → yüksek dayanım/uzama. A356-T benzeri ampirik eğriler.
   */
  private mekanikOzet(doluluk: Uint8Array, toplam: number, sdasUm: Float32Array): IMekanikOzet | undefined {
    let mn = Infinity, mx = 0, top = 0, say = 0;
    for (let i = 0; i < toplam; i++) {
      if (doluluk[i] !== 1) continue;
      const s = sdasUm[i];
      if (!isFinite(s) || s <= 0) continue;
      if (s < mn) mn = s;
      if (s > mx) mx = s;
      top += s; say++;
    }
    if (say === 0) return undefined;
    const ort = top / say;
    const clamp = (v: number, a: number, b: number) => Math.min(b, Math.max(a, v));
    // SDAS 20µm referans; her +µm dayanımı düşürür (A356 ampirik)
    const uts = clamp(300 - 1.8 * (ort - 20), 150, 320);
    const akma = clamp(230 - 1.0 * (ort - 20), 120, 250);
    const uzama = clamp(12 - 0.18 * (ort - 20), 1, 14);
    const sertlik = uts / 3.4; // HB ≈ UTS/3.4
    return {
      sdasOrtUm: ort, sdasMinUm: isFinite(mn) ? mn : 0, sdasMaxUm: mx,
      utsMpa: uts, akmaMpa: akma, uzamaYuzde: uzama, sertlikHB: sertlik,
    };
  }

  // ═══════════════════════════════════════════════════════════════════════
  // Entalpi <-> sıcaklık dönüşümleri
  // ═══════════════════════════════════════════════════════════════════════

  /** Hacimsel entalpi H_v(T) = ρ·cₚ·T + ρ·L·(1 − f_s)  [J/m³] */
  private entalpiHv(Tv: number, a: IAlasimTermal, rho: number, rhoL: number): number {
    const cp = a.ozguIsi(Tv);
    const fs = a.katiFraksiyon(Tv);
    return rho * cp * Tv + rhoL * (1 - fs);
  }

  /**
   * H_v → T ters dönüşüm.
   *   H_v_sol = ρcₚ·T_sol               (katı sınırı, fs=1)
   *   H_v_liq = ρcₚ·T_liq + ρL          (sıvı sınırı, fs=0)
   *   H_v ≥ H_v_liq → T = (H_v − ρL)/(ρcₚ)      [sıvı]
   *   H_v ≤ H_v_sol → T = H_v/(ρcₚ)              [katı]
   *   arada (mushy) → bisection ile H_v(T)=hedef kökü (T_sol..T_liq).
   */
  private sicaklikGeriCevir(
    Hv: number, a: IAlasimTermal, rho: number, rhoL: number,
    T_sol: number, T_liq: number,
  ): number {
    const cpSol = a.ozguIsi(T_sol);
    const cpLiq = a.ozguIsi(T_liq);
    const HvSol = rho * cpSol * T_sol;             // fs=1 → gizli ısı yok
    const HvLiq = rho * cpLiq * T_liq + rhoL;      // fs=0 → tam gizli ısı

    if (Hv >= HvLiq) {
      // Sıvı: gizli ısı dolu, cₚ liq ile çöz
      return (Hv - rhoL) / (rho * cpLiq);
    }
    if (Hv <= HvSol) {
      // Katı: gizli ısı yok
      return Hv / (rho * cpSol);
    }
    // Mushy: H_v(T) = ρcₚ(T)·T + ρL·(1 − f_s(T)) = Hv kökünü bisection ile bul.
    let lo = T_sol, hi = T_liq;
    let fLo = this.entalpiHv(lo, a, rho, rhoL) - Hv;
    // f_s monoton azaldığından H_v(T) T ile monoton artar → bisection güvenli.
    for (let it = 0; it < 24; it++) {
      const mid = 0.5 * (lo + hi);
      const fMid = this.entalpiHv(mid, a, rho, rhoL) - Hv;
      if (Math.abs(fMid) < 1) return mid;        // J/m³ toleransı
      if ((fMid > 0) === (fLo > 0)) { lo = mid; fLo = fMid; }
      else hi = mid;
    }
    return 0.5 * (lo + hi);
  }

  // ═══════════════════════════════════════════════════════════════════════
  // Gradyan / izleme yardımcıları (allocation yok)
  // ═══════════════════════════════════════════════════════════════════════

  /**
   * Merkezi fark (tek eksen) — komşu dolu değilse tek taraflı/0.
   * Dönüş: ΔT (komşular arası), çağıran dxMm'e böler.
   */
  private merkeziFark(
    T: Float32Array, doluluk: Uint8Array, i: number, _step: number,
    artiVar: boolean, eksiVar: boolean, offArti: number, offEksi: number,
  ): number {
    const ip = i + offArti, im = i + offEksi;
    const pVar = artiVar && doluluk[ip] === 1;
    const mVar = eksiVar && doluluk[im] === 1;
    if (pVar && mVar) return (T[ip] - T[im]) * 0.5; // merkezi
    if (pVar) return T[ip] - T[i];                   // ileri
    if (mVar) return T[i] - T[im];                   // geri
    return 0;
  }

  /**
   * Artık gerilim + distorsiyon (termoelastik tahmin, MPa):
   * Komşular farklı zamanda katılaşırsa büzülme uyumsuzluğu (differential
   * contraction) → gerinim ε ≈ α·ΔT_etkin·fark; gerilim σ = min(E·ε, akma_sıcak)
   * (plastik tavan). Distorsiyon: ortalama uyumsuz gerinim × parça boyu (mm) +
   * 0-1 indeks (süre dağılımı CoV + yüksek-stres oranı).
   */
  private gerilmeAnaliz(
    k: IVoxelKafes, katilasmaSuresi: Float32Array, doluluk: Uint8Array, toplam: number,
    alasim?: IAlasimTermal, kalipT = 25,
  ): {
    bolgeler: { merkez: THREE.Vector3; siddet: number }[];
    distorsiyon: number;
    gerilmeMpa: Float32Array;
    maxGerilmeMpa: number;
    distorsiyonMm: number;
  } {
    const [nx, ny, nz] = k.boyut;
    const nxny = nx * ny;
    const off = [1, -1, nx, -nx, nxny, -nxny];
    const stres = new Float32Array(toplam);
    const gerilmeMpa = new Float32Array(toplam);
    // Termoelastik parametreler (Al varsayılanları)
    const E = (alasim?.elastikModulGpa ?? 70) * 1000;          // MPa
    const alfa = alasim?.termalGenlesme ?? 22e-6;               // 1/K
    const akma = alasim?.akmaSicakMpa ?? 180;                    // MPa (plastik tavan)
    const dT = Math.max(50, (alasim?.T_solidus ?? 550) - kalipT); // etkin soğuma aralığı
    let ortT = 0, sayT = 0;
    for (let i = 0; i < toplam; i++) if (doluluk[i] === 1) { ortT += katilasmaSuresi[i]; sayT++; }
    ortT = sayT ? ortT / sayT : 1;
    let varTop = 0, yuksek = 0, maxMpa = 0, epsTop = 0;
    for (let i = 0; i < toplam; i++) {
      if (doluluk[i] !== 1) continue;
      const ti = katilasmaSuresi[i];
      varTop += (ti - ortT) * (ti - ortT);
      const ix = i % nx, iy = ((i / nx) | 0) % ny, iz = (i / nxny) | 0;
      let maxFark = 0;
      for (let c = 0; c < 6; c++) {
        if (c === 0 && ix + 1 >= nx) continue;
        if (c === 1 && ix - 1 < 0) continue;
        if (c === 2 && iy + 1 >= ny) continue;
        if (c === 3 && iy - 1 < 0) continue;
        if (c === 4 && iz + 1 >= nz) continue;
        if (c === 5 && iz - 1 < 0) continue;
        const v = i + off[c];
        if (v < 0 || v >= toplam || doluluk[v] !== 1) continue;
        const fark = Math.abs(ti - katilasmaSuresi[v]) / Math.max(ti, katilasmaSuresi[v], 1e-3);
        if (fark > maxFark) maxFark = fark;
      }
      stres[i] = maxFark;
      // σ = min(E·α·ΔT·fark, akma) — komşu tam farklı büzülürse termoelastik üst sınır
      const eps = alfa * dT * maxFark;
      const sigma = Math.min(E * eps, akma);
      gerilmeMpa[i] = sigma;
      if (sigma > maxMpa) maxMpa = sigma;
      epsTop += eps;
      if (maxFark > 0.5) yuksek++;
    }
    const std = Math.sqrt(varTop / Math.max(sayT, 1));
    const cov = std / Math.max(ortT, 1e-3);
    const distorsiyon = Math.min(1, cov * 0.6 + (yuksek / Math.max(sayT, 1)) * 2);
    // Çarpılma tahmini: ortalama uyumsuz gerinim × parçanın en uzun boyu
    const boyMm = Math.max(nx, ny, nz) * k.hucreMm;
    const distorsiyonMm = sayT ? (epsTop / sayT) * boyMm : 0;
    const bolgeler = this.bolgeKumele(k, stres, 0.5);
    return { bolgeler, distorsiyon, gerilmeMpa, maxGerilmeMpa: maxMpa, distorsiyonMm };
  }

  private doluSay(doluluk: Uint8Array): number {
    let n = 0;
    for (let i = 0; i < doluluk.length; i++) if (doluluk[i] === 1) n++;
    return n;
  }

  /**
   * Soğutucu (chill) ısı çekiş alanı: chill küresi içindeki dolu voxellere
   * ekstra ısı iletkenliği hA·çarpan atar (Float32, [W/K]). Çakışmada max.
   * Varsayılan çarpan ~6 (metal kalıp ≈ kuma göre).
   */
  private sogutucuAlani(
    k: IVoxelKafes,
    sogutucular: { pozisyon: THREE.Vector3; capMm: number; hCarpan?: number }[],
    hA: number,
  ): Float32Array {
    const [nx, ny, nz] = k.boyut;
    const out = new Float32Array(nx * ny * nz);
    const doluluk = k.doluluk;
    const hh = k.hucreMm;
    for (const s of sogutucular) {
      const deger = hA * Math.max(1, s.hCarpan ?? 6);
      const r = Math.max(s.capMm * 0.5, hh);
      const cx = (s.pozisyon.x - k.origin.x) / hh;
      const cy = (s.pozisyon.y - k.origin.y) / hh;
      const cz = (s.pozisyon.z - k.origin.z) / hh;
      const rv = r / hh, rv2 = rv * rv;
      const x0 = Math.max(0, Math.floor(cx - rv)), x1 = Math.min(nx - 1, Math.ceil(cx + rv));
      const y0 = Math.max(0, Math.floor(cy - rv)), y1 = Math.min(ny - 1, Math.ceil(cy + rv));
      const z0 = Math.max(0, Math.floor(cz - rv)), z1 = Math.min(nz - 1, Math.ceil(cz + rv));
      for (let z = z0; z <= z1; z++) {
        const dz = z + 0.5 - cz;
        for (let y = y0; y <= y1; y++) {
          const dy = y + 0.5 - cy;
          for (let x = x0; x <= x1; x++) {
            const dx = x + 0.5 - cx;
            if (dx * dx + dy * dy + dz * dz > rv2) continue;
            const lin = x + nx * (y + ny * z);
            if (doluluk[lin] === 1 && deger > out[lin]) out[lin] = deger;
          }
        }
      }
    }
    return out;
  }

  /**
   * Maça gazı (binder gas) blowhole riski: maça kabuğu çevresindeki (yarıçap +
   * 3 hücre) metal voxelleri, toplam sürenin %35'inden uzun sıvı kaldıysa gaz
   * yutma adayıdır. Yakın hava deliği riski %70 azaltır (gaz tahliyesi).
   */
  private macaGazAnaliz(
    k: IVoxelKafes, katilasmaSuresi: Float32Array, doluluk: Uint8Array, toplamSure: number,
    macalar: { pozisyon: THREE.Vector3; capMm: number }[],
    havaDelikleri: { pozisyon: THREE.Vector3; capMm: number }[],
  ): { merkez: THREE.Vector3; siddet: number }[] {
    const [nx, ny, nz] = k.boyut;
    const toplam = nx * ny * nz;
    const h = k.hucreMm;
    const risk = new Float32Array(toplam);
    const esikSure = toplamSure * 0.35;

    for (const m of macalar) {
      // Yakın hava deliği var mı → tahliye faktörü
      let ventFak = 1;
      for (const v of havaDelikleri) {
        const erisim = (m.capMm + v.capMm) * 0.5 + 25; // mm pay
        if (v.pozisyon.distanceTo(m.pozisyon) <= erisim) { ventFak = 0.3; break; }
      }
      const rIc = m.capMm * 0.5;              // maça yüzeyi
      const rDis = rIc + 3 * h;               // gaz nüfuz kabuğu
      const rIc2 = rIc * rIc, rDis2 = rDis * rDis;
      const cx = (m.pozisyon.x - k.origin.x) / h;
      const cy = (m.pozisyon.y - k.origin.y) / h;
      const cz = (m.pozisyon.z - k.origin.z) / h;
      const rv = rDis / h;
      const x0 = Math.max(0, Math.floor(cx - rv)), x1 = Math.min(nx - 1, Math.ceil(cx + rv));
      const y0 = Math.max(0, Math.floor(cy - rv)), y1 = Math.min(ny - 1, Math.ceil(cy + rv));
      const z0 = Math.max(0, Math.floor(cz - rv)), z1 = Math.min(nz - 1, Math.ceil(cz + rv));
      for (let z = z0; z <= z1; z++) {
        const dz = (z + 0.5 - cz) * h;
        for (let y = y0; y <= y1; y++) {
          const dy = (y + 0.5 - cy) * h;
          for (let x = x0; x <= x1; x++) {
            const dx = (x + 0.5 - cx) * h;
            const d2 = dx * dx + dy * dy + dz * dz;
            if (d2 < rIc2 || d2 > rDis2) continue;      // sadece kabuk
            const lin = x + nx * (y + ny * z);
            if (doluluk[lin] !== 1) continue;
            const ts = katilasmaSuresi[lin];
            if (ts <= esikSure) continue;                // erken dondu → gaz giremez
            const sureRisk = Math.min(1, (ts - esikSure) / Math.max(toplamSure - esikSure, 1e-3));
            const r = sureRisk * ventFak;
            if (r > risk[lin]) risk[lin] = r;
          }
        }
      }
    }
    return this.bolgeKumele(k, risk, 0.3);
  }

  // ═══════════════════════════════════════════════════════════════════════
  // Clyne-Davies sıcak yırtılma duyarlılığı (CSC)
  // ═══════════════════════════════════════════════════════════════════════

  /**
   * CSC = t_v / t_r:
   *   t_v = t99 − t90  (kırılgan aralık: f_s 0.9→0.99, sıvı besleme zorlanır)
   *   t_r = t90 − t40  (gevşeme/serbest besleme aralığı: f_s 0.4→0.9)
   * Yüksek oran = uzun kırılgan kalış → sıcak yırtılma riski. 0-1 normalize.
   */
  private clyneDavies(
    doluluk: Uint8Array, toplam: number,
    t40: Float32Array, t90: Float32Array, t99: Float32Array,
    cikti: Float32Array,
  ): void {
    for (let i = 0; i < toplam; i++) {
      if (doluluk[i] !== 1) continue;
      const a40 = t40[i], a90 = t90[i], a99 = t99[i];
      if (a90 < 0 || a40 < 0) continue;            // mushy'e hiç girmedi
      const tv = a99 >= 0 ? Math.max(0, a99 - a90) : Math.max(0, a90 - a90);
      const tr = Math.max(1e-6, a90 - a40);
      // CSC ham oranı; pratikte 1 civarı kritik → 0-1'e sıkıştır.
      const csc = tv / tr;
      cikti[i] = Math.min(1, csc);
    }
  }

  // ═══════════════════════════════════════════════════════════════════════
  // Kusur bölgesi tespiti (bağlı bileşen kümeleme, 6-bağ)
  // ═══════════════════════════════════════════════════════════════════════

  /**
   * Sıcak noktalar: en yüksek katilasmaSuresi (üst ~%5) → bağlı bileşene
   * kümele. beslendi: yakın çevresinde (birkaç hücre) DAHA GEÇ katılaşan
   * besleyici voxel varsa true, yoksa beslenmemiş çekme adayı.
   */
  private sicakNoktaBul(
    k: IVoxelKafes, katilasmaSuresi: Float32Array,
  ): { merkez: THREE.Vector3; katilasmaSuresi: number; beslendi: boolean }[] {
    const [nx, ny, nz] = k.boyut;
    const toplam = nx * ny * nz;
    const doluluk = k.doluluk;
    const nxny = nx * ny;

    // Üst %5 eşiği: dolu voxel sürelerinin maksimumuna göre.
    let maxSure = 0;
    for (let i = 0; i < toplam; i++) {
      if (doluluk[i] === 1 && katilasmaSuresi[i] > maxSure) maxSure = katilasmaSuresi[i];
    }
    if (maxSure <= 0) return [];
    const esik = maxSure * 0.95;

    const sicak = new Uint8Array(toplam);
    for (let i = 0; i < toplam; i++) {
      if (doluluk[i] === 1 && katilasmaSuresi[i] >= esik) sicak[i] = 1;
    }

    const ziyaret = new Uint8Array(toplam);
    const yigin = new Int32Array(toplam);
    const off = [1, -1, nx, -nx, nxny, -nxny];
    const merkez = new THREE.Vector3();
    const sonuc: { merkez: THREE.Vector3; katilasmaSuresi: number; beslendi: boolean }[] = [];

    for (let s = 0; s < toplam; s++) {
      if (sicak[s] !== 1 || ziyaret[s]) continue;
      let yN = 0;
      yigin[yN++] = s; ziyaret[s] = 1;
      let cx = 0, cy = 0, cz = 0, say = 0, sureMax = 0;
      while (yN > 0) {
        const u = yigin[--yN];
        const ux = u % nx, uy = ((u / nx) | 0) % ny, uz = (u / nxny) | 0;
        cx += ux; cy += uy; cz += uz; say++;
        if (katilasmaSuresi[u] > sureMax) sureMax = katilasmaSuresi[u];
        for (let c = 0; c < 6; c++) {
          const v = u + off[c];
          if (v < 0 || v >= toplam || ziyaret[v] || sicak[v] !== 1) continue;
          if (c === 0 && ux + 1 >= nx) continue;
          if (c === 1 && ux - 1 < 0) continue;
          if (c === 2 && uy + 1 >= ny) continue;
          if (c === 3 && uy - 1 < 0) continue;
          if (c === 4 && uz + 1 >= nz) continue;
          if (c === 5 && uz - 1 < 0) continue;
          ziyaret[v] = 1;
          yigin[yN++] = v;
        }
      }
      if (say < 3) continue;
      const mix = Math.round(cx / say);
      const miy = Math.round(cy / say);
      const miz = Math.round(cz / say);
      const linMerkez = Math.min(toplam - 1, Math.max(0, mix + nx * (miy + ny * miz)));
      voxelMerkez(k, linMerkez, merkez);
      // Beslenme: merkez çevresinde (±2 hücre) DAHA GEÇ katılaşan voxel var mı?
      const beslendi = this.besleyiciVarMi(
        k, mix, miy, miz, sureMax, katilasmaSuresi, 2,
      );
      sonuc.push({ merkez: merkez.clone(), katilasmaSuresi: sureMax, beslendi });
    }
    return sonuc;
  }

  /** Merkez çevresinde r hücre içinde, referanstan DAHA GEÇ katılaşan voxel var mı? */
  private besleyiciVarMi(
    k: IVoxelKafes, cx: number, cy: number, cz: number,
    ref: number, katilasmaSuresi: Float32Array, r: number,
  ): boolean {
    const [nx, ny, nz] = k.boyut;
    const doluluk = k.doluluk;
    const nxny = nx * ny;
    for (let dz = -r; dz <= r; dz++) {
      const z = cz + dz; if (z < 0 || z >= nz) continue;
      for (let dy = -r; dy <= r; dy++) {
        const y = cy + dy; if (y < 0 || y >= ny) continue;
        for (let dx = -r; dx <= r; dx++) {
          const x = cx + dx; if (x < 0 || x >= nx) continue;
          if (dx === 0 && dy === 0 && dz === 0) continue;
          const lin = x + nx * (y + ny * z);
          if (doluluk[lin] === 1 && katilasmaSuresi[lin] > ref + 1e-3) return true;
        }
      }
    }
    return false;
  }

  /**
   * Çekme bölgeleri: Niyama < eşik voxel'leri kümele.
   * şiddet = (eşik − Ny)/eşik clamp 0-1.
   */
  private cekmeBul(
    k: IVoxelKafes, niyama: Float32Array, doluluk: Uint8Array,
  ): { merkez: THREE.Vector3; siddet: number }[] {
    const [nx, ny, nz] = k.boyut;
    const toplam = nx * ny * nz;
    const esik = FikoKatilasmaSimulasyon.NIYAMA_ESIK;
    const siddetAlan = new Float32Array(toplam);
    for (let i = 0; i < toplam; i++) {
      if (doluluk[i] !== 1) continue;
      const ny_ = niyama[i];
      if (ny_ > 0 && ny_ < esik) {
        siddetAlan[i] = Math.min(1, (esik - ny_) / esik);
      }
    }
    return this.siddetKumele(k, siddetAlan);
  }

  /** Bir eşiğin üstündeki sicakYirtilma voxel'lerini şiddet bölgelerine kümele. */
  private bolgeKumele(
    k: IVoxelKafes, alan: Float32Array, esik: number,
  ): { merkez: THREE.Vector3; siddet: number }[] {
    const toplam = alan.length;
    const doluluk = k.doluluk;
    const siddetAlan = new Float32Array(toplam);
    for (let i = 0; i < toplam; i++) {
      if (doluluk[i] === 1 && alan[i] > esik) siddetAlan[i] = alan[i];
    }
    return this.siddetKumele(k, siddetAlan);
  }

  /**
   * Ortak şiddet kümeleyici: siddetAlan>0 voxel'leri 6-bağ bağlı bileşene
   * böl, <3 hücre ele, merkez + ortalama şiddet üret.
   */
  private siddetKumele(
    k: IVoxelKafes, siddetAlan: Float32Array,
  ): { merkez: THREE.Vector3; siddet: number }[] {
    const [nx, ny, nz] = k.boyut;
    const toplam = nx * ny * nz;
    const nxny = nx * ny;
    const ziyaret = new Uint8Array(toplam);
    const yigin = new Int32Array(toplam);
    const off = [1, -1, nx, -nx, nxny, -nxny];
    const merkez = new THREE.Vector3();
    const sonuc: { merkez: THREE.Vector3; siddet: number }[] = [];

    for (let s = 0; s < toplam; s++) {
      if (siddetAlan[s] <= 0 || ziyaret[s]) continue;
      let yN = 0;
      yigin[yN++] = s; ziyaret[s] = 1;
      let cx = 0, cy = 0, cz = 0, say = 0, siddetTop = 0;
      while (yN > 0) {
        const u = yigin[--yN];
        const ux = u % nx, uy = ((u / nx) | 0) % ny, uz = (u / nxny) | 0;
        cx += ux; cy += uy; cz += uz; say++;
        siddetTop += siddetAlan[u];
        for (let c = 0; c < 6; c++) {
          const v = u + off[c];
          if (v < 0 || v >= toplam || ziyaret[v] || siddetAlan[v] <= 0) continue;
          if (c === 0 && ux + 1 >= nx) continue;
          if (c === 1 && ux - 1 < 0) continue;
          if (c === 2 && uy + 1 >= ny) continue;
          if (c === 3 && uy - 1 < 0) continue;
          if (c === 4 && uz + 1 >= nz) continue;
          if (c === 5 && uz - 1 < 0) continue;
          ziyaret[v] = 1;
          yigin[yN++] = v;
        }
      }
      if (say < 3) continue;
      const linMerkez = Math.min(toplam - 1, Math.max(0,
        Math.round(cx / say) + nx * (Math.round(cy / say) + ny * Math.round(cz / say))));
      voxelMerkez(k, linMerkez, merkez);
      sonuc.push({ merkez: merkez.clone(), siddet: Math.min(1, siddetTop / say) });
    }
    return sonuc;
  }

  // ═══════════════════════════════════════════════════════════════════════
  // Termal çözüm kafesi — büyük gridlerde 2× downsample (performans)
  // ═══════════════════════════════════════════════════════════════════════

  /**
   * Termal çözüm kafesi seçimi — 2^L kabalaştırma (L = 0..3).
   * İki kriter birlikte:
   *  1. Dolu voxel sayısı DOWNSAMPLE_ESIK altına insin (performans).
   *  2. Kararlı Δt ile MAX_ADIM adımda hedef süre KAPSANSIN (dt ∝ dx²) —
   *     yoksa explicit FDM ya kararsızlaşır ya da süre yarıda kalır.
   * Sonuç alanları + termal/boşluk kareleri geriOlcekle/kareUpscale ile
   * orijinal (ince) kafese yayılır → görsel her zaman ince gridde.
   */
  private cozumKafesi(girdi: IKatilasmaGirdi, alphaMax: number): {
    kafes: IVoxelKafes;
    baslangicT: Float32Array | null;
    seviye: number;
    geriOlcekle: (alanlar: {
      katilasmaSuresi: Float32Array;
      niyama: Float32Array;
      sicakYirtilma: Float32Array;
      sdasUm: Float32Array;
    }) => {
      katilasmaSuresi: Float32Array;
      niyama: Float32Array;
      sicakYirtilma: Float32Array;
      sdasUm: Float32Array;
    };
    /** Kaba grid U8/F32 alanını ince gride yay (kare/boşluk upscale) */
    kareUpscaleU8: (kaba: Uint8Array) => Uint8Array;
    kareUpscaleF32: (kaba: Float32Array) => Float32Array;
  } {
    const k = girdi.kafes;
    const dolu = k.doluSayi > 0 ? k.doluSayi : this.doluSay(k.doluluk);
    const maxAdim = FikoKatilasmaSimulasyon.MAX_ADIM;
    const hedefSure = girdi.maxSure && girdi.maxSure > 0
      ? girdi.maxSure : FikoKatilasmaSimulasyon.VARSAYILAN_MAX_SURE;

    // Seviye seç: hem hücre sayısı hem zaman kapsaması sağlanana dek L++ (tavan 3)
    let L = 0;
    for (; L < 3; L++) {
      const dxL = (k.hucreMm * (1 << L)) / 1000;
      const dtL = 0.9 * (dxL * dxL) / (6 * Math.max(alphaMax, 1e-9));
      const kapsamaOk = dtL * maxAdim >= hedefSure;
      const sayiOk = dolu / Math.pow(8, L) <= FikoKatilasmaSimulasyon.DOWNSAMPLE_ESIK;
      if (kapsamaOk && sayiOk) break;
    }

    if (L === 0) {
      const kimlikU8 = (kaba: Uint8Array) => kaba;
      const kimlikF32 = (kaba: Float32Array) => kaba;
      return {
        kafes: k,
        baslangicT: girdi.baslangicT ?? null,
        seviye: 0,
        geriOlcekle: (alanlar) => alanlar,
        kareUpscaleU8: kimlikU8,
        kareUpscaleF32: kimlikF32,
      };
    }

    // 2^L coarsen: her (2^L)³ blok bir kaba hücre. Blokta ≥1 dolu varsa dolu.
    const [nx, ny, nz] = k.boyut;
    const cnx = Math.max(1, Math.ceil(nx / (1 << L)));
    const cny = Math.max(1, Math.ceil(ny / (1 << L)));
    const cnz = Math.max(1, Math.ceil(nz / (1 << L)));
    const cToplam = cnx * cny * cnz;
    const cDoluluk = new Uint8Array(cToplam);
    const cKalinlik = new Float32Array(cToplam);
    const cBaslangic = girdi.baslangicT ? new Float32Array(cToplam) : null;
    const cBolgeTipi = k.bolgeTipi ? new Uint8Array(cToplam) : null;
    const cBesIdx = k.besleyiciIdx ? new Int16Array(cToplam).fill(-1) : null;
    const sayAlan = new Uint32Array(cToplam);
    const baslangicT = girdi.baslangicT;

    let cDolu = 0;
    for (let z = 0; z < nz; z++) {
      const cz = z >> L;
      for (let y = 0; y < ny; y++) {
        const cy = y >> L;
        for (let x = 0; x < nx; x++) {
          const lin = x + nx * (y + ny * z);
          if (k.doluluk[lin] !== 1) continue;
          const cLin = (x >> L) + cnx * (cy + cny * cz);
          if (cDoluluk[cLin] === 0) { cDoluluk[cLin] = 1; cDolu++; }
          if (k.kalinlik[lin] > cKalinlik[cLin]) cKalinlik[cLin] = k.kalinlik[lin];
          if (cBaslangic && baslangicT) cBaslangic[cLin] += baslangicT[lin];
          if (cBolgeTipi && k.bolgeTipi && k.bolgeTipi[lin] > cBolgeTipi[cLin]) cBolgeTipi[cLin] = k.bolgeTipi[lin];
          if (cBesIdx && k.besleyiciIdx && k.besleyiciIdx[lin] >= 0 && cBesIdx[cLin] < 0) cBesIdx[cLin] = k.besleyiciIdx[lin];
          sayAlan[cLin]++;
        }
      }
    }
    if (cBaslangic) {
      for (let i = 0; i < cToplam; i++) if (sayAlan[i] > 0) cBaslangic[i] /= sayAlan[i];
    }

    const cKafes: IVoxelKafes = {
      boyut: [cnx, cny, cnz],
      hucreMm: k.hucreMm * (1 << L),
      origin: k.origin.clone(),
      doluluk: cDoluluk,
      doluSayi: cDolu,
      kalinlik: cKalinlik,
      hacimCm3: k.hacimCm3,
      bolgeTipi: cBolgeTipi ?? undefined,
      besleyiciIdx: cBesIdx ?? undefined,
    };

    // İnce lin → kaba lin eşleme (upscale hızlansın diye tek sefer)
    const toplamInce = nx * ny * nz;
    const inceKaba = new Int32Array(toplamInce).fill(-1);
    for (let z = 0; z < nz; z++) {
      const cz = z >> L;
      for (let y = 0; y < ny; y++) {
        const cy = y >> L;
        const satir = nx * (y + ny * z);
        const cTaban = cnx * (cy + cny * cz);
        for (let x = 0; x < nx; x++) {
          const lin = satir + x;
          if (k.doluluk[lin] === 1) inceKaba[lin] = (x >> L) + cTaban;
        }
      }
    }

    const geriOlcekle = (alanlar: {
      katilasmaSuresi: Float32Array; niyama: Float32Array; sicakYirtilma: Float32Array; sdasUm: Float32Array;
    }) => {
      const oKatilasma = new Float32Array(toplamInce);
      const oNiyama = new Float32Array(toplamInce);
      const oYirtilma = new Float32Array(toplamInce);
      const oSdas = new Float32Array(toplamInce);
      for (let lin = 0; lin < toplamInce; lin++) {
        const c = inceKaba[lin];
        if (c < 0) continue;
        oKatilasma[lin] = alanlar.katilasmaSuresi[c];
        oNiyama[lin] = alanlar.niyama[c];
        oYirtilma[lin] = alanlar.sicakYirtilma[c];
        oSdas[lin] = alanlar.sdasUm[c];
      }
      return { katilasmaSuresi: oKatilasma, niyama: oNiyama, sicakYirtilma: oYirtilma, sdasUm: oSdas };
    };

    const kareUpscaleU8 = (kaba: Uint8Array): Uint8Array => {
      const out = new Uint8Array(toplamInce);
      for (let lin = 0; lin < toplamInce; lin++) {
        const c = inceKaba[lin];
        if (c >= 0) out[lin] = kaba[c];
      }
      return out;
    };
    const kareUpscaleF32 = (kaba: Float32Array): Float32Array => {
      const out = new Float32Array(toplamInce);
      for (let lin = 0; lin < toplamInce; lin++) {
        const c = inceKaba[lin];
        if (c >= 0) out[lin] = kaba[c];
      }
      return out;
    };

    return { kafes: cKafes, baslangicT: cBaslangic, seviye: L, geriOlcekle, kareUpscaleU8, kareUpscaleF32 };
  }
}
